import React, { useLayoutEffect, useRef, useState } from 'react';
import { DialogComponent } from '@syncfusion/ej2-react-popups';
import { ButtonComponent } from '@syncfusion/ej2-react-buttons';
import { useTranslation } from 'react-i18next';
import { useErrorHandler } from '../../utils/errorHandler';
import { useToast } from '../../hooks/useToast';
import { OpenAPI } from '../../api/core/OpenAPI';
import { accessTokenStore } from '../../auth/accessTokenStore';
import ScheduleViewContent from '../../components/ScheduleViewContent';
import { useHasPermission } from '../../utils/permissions';

interface OptimizedScheduleViewDialogProps {
  scheduleUuid: string;
  scheduleName?: string;
  isOpen: boolean;
  onClose: () => void;
}

const OptimizedScheduleViewDialog: React.FC<OptimizedScheduleViewDialogProps> = ({
  scheduleUuid,
  scheduleName,
  isOpen,
  onClose
}) => {
  const { t, i18n } = useTranslation(['common']);
  const { handleError } = useErrorHandler();
  const { showToast } = useToast();
  const [isExporting, setIsExporting] = useState(false);
  const hasExportPermission = useHasPermission('reports:export');
  const [contentHeightPx, setContentHeightPx] = useState(320);
  const lastContentHeightRef = useRef(320);
  const rafIdRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    if (!isOpen) {
      return;
    }

    const updateContentHeight = () => {
      const dialogEl = document.querySelector('.schedule-view-dialog') as HTMLElement | null;
      const contentEl = dialogEl?.querySelector('.e-dlg-content') as HTMLElement | null;
      if (!contentEl) return;
      const styles = window.getComputedStyle(contentEl);
      const paddingTop = parseFloat(styles.paddingTop || '0');
      const paddingBottom = parseFloat(styles.paddingBottom || '0');
      const innerContentHeight = contentEl.clientHeight - paddingTop - paddingBottom;
      const wrapperPadding = 32; // p-4 on the content wrapper
      const available = Math.floor(innerContentHeight - wrapperPadding);
      if (available > 0 && Math.abs(available - lastContentHeightRef.current) > 2) {
        lastContentHeightRef.current = available;
        setContentHeightPx(available);
      }
    };

    const scheduleUpdate = () => {
      if (rafIdRef.current != null) cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = requestAnimationFrame(() => {
        rafIdRef.current = null;
        updateContentHeight();
      });
    };
    scheduleUpdate();
    window.addEventListener('resize', scheduleUpdate);

    const resizeObserver = new ResizeObserver(scheduleUpdate);
    const dialogEl = document.querySelector('.schedule-view-dialog') as HTMLElement | null;
    if (dialogEl) {
      resizeObserver.observe(dialogEl);
    }
    const contentEl = dialogEl?.querySelector('.e-dlg-content') as HTMLElement | null;
    if (contentEl) {
      resizeObserver.observe(contentEl);
    }

    return () => {
      if (rafIdRef.current != null) cancelAnimationFrame(rafIdRef.current);
      window.removeEventListener('resize', scheduleUpdate);
      resizeObserver.disconnect();
    };
  }, [isOpen]);

  // Format title with actual schedule name
  const dialogTitle = scheduleName 
    ? t('productionSchedule.optimizedScheduleView.title', 'Optimierter Plan - {scheduleName}', { scheduleName }).replace('{scheduleName}', scheduleName)
    : t('productionSchedule.optimizedScheduleView.title', 'Optimierter Plan - {scheduleName}', { scheduleName: scheduleUuid }).replace('{scheduleName}', scheduleUuid);

  const getAuthHeaders = async () => {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    if (i18n.language) {
      headers['Accept-Language'] = i18n.language;
    }
    const token = accessTokenStore.get();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const storedTenant = localStorage.getItem('selectedTenant');
    if (storedTenant) {
      const tenant = JSON.parse(storedTenant);
      headers['X-Tenant-ID'] = tenant.uuid;
    }
    return headers;
  };


  const decodeRfc2047 = (value: string) => {
    const match = value.match(/=\?([^?]+)\?([bqBQ])\?([^?]+)\?=/);
    if (!match) return value;
    const encoding = match[2].toLowerCase();
    const text = match[3];
    try {
      if (encoding === 'q') {
        const q = text.replace(/_/g, ' ').replace(/=([0-9A-Fa-f]{2})/g, '%$1');
        return decodeURIComponent(q);
      }
      const binary = atob(text);
      const percent = Array.from(binary, (char) =>
        '%' + char.charCodeAt(0).toString(16).padStart(2, '0')
      ).join('');
      return decodeURIComponent(percent);
    } catch {
      return value;
    }
  };

  const parseFilename = (contentDisposition: string | null, fallback: string) => {
    if (!contentDisposition) return fallback;
    const filenameStarMatch = contentDisposition.match(/filename\*\s*=\s*([^;]+)/i);
    const filenameMatch = contentDisposition.match(/filename\s*=\s*([^;]+)/i);
    const raw = filenameStarMatch?.[1] ?? filenameMatch?.[1];
    if (!raw) return fallback;
    const cleaned = raw.trim().replace(/^"|"$/g, '');
    const decoded = decodeRfc2047(cleaned);
    if (/^UTF-8''/i.test(decoded)) {
      const withoutPrefix = decoded.replace(/^UTF-8''/i, '');
      try {
        return decodeURIComponent(withoutPrefix);
      } catch {
        return withoutPrefix;
      }
    }
    return decoded;
  };

  const handleExportToExcel = async () => {
    if (isExporting) {
      return;
    }
    setIsExporting(true);

    try {
      const apiBaseUrl = OpenAPI.BASE || '';
      const url = `${apiBaseUrl}/api/excel-exports/production-schedules/${scheduleUuid}`;
      const response = await fetch(url, {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify({})
      });

      if (!response.ok) {
        throw new Error('Failed to export excel result');
      }

      const blob = await response.blob();
      const now = new Date();
      const ts = now.toISOString().replace(/[-:T]/g, '').slice(2, 13);
      const fallbackName = scheduleName
        ? `${scheduleName.replace(/[^a-zA-Z0-9äöüÄÖÜß]/g, '_')}_${ts}.xlsx`
        : `Plan_${ts}.xlsx`;
      const filename = parseFilename(response.headers.get('Content-Disposition'), fallbackName);

      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      showToast('success', t('common.success', 'Erfolg'), t('productionSchedule.optimizedScheduleView.exportExcelSuccess', 'Excel Export erfolgreich'));
    } catch (error) {
      handleError(error, 'exporting excel result');
      showToast('error', t('common.error'), t('productionSchedule.optimizedScheduleView.exportExcelError', 'Excel Export fehlgeschlagen'));
    } finally {
      setIsExporting(false);
    }
  };

  const footerTemplate = () => (
    <div className="flex justify-end gap-3 p-4">
      <ButtonComponent
        onClick={handleExportToExcel}
        cssClass="e-outline"
        iconCss="e-icons e-download"
        disabled={isExporting || !hasExportPermission}
        type="button"
      >
        {t('productionSchedule.optimizedScheduleView.exportExcel', 'Excel Export')}
      </ButtonComponent>
      <ButtonComponent
        onClick={onClose}
        cssClass="e-primary"
        type="button"
      >
        {t('actions.close', 'Schließen')}
      </ButtonComponent>
    </div>
  );

  return (
    <DialogComponent
      visible={isOpen}
      isModal={true}
      cssClass="schedule-view-dialog"
      header={dialogTitle}
      showCloseIcon={true}
      closeOnEscape={true}
      width="90%"
      height="700px"
      close={onClose}
      animationSettings={{ effect: 'None' }}
      footerTemplate={footerTemplate}
    >
      <div className="p-4 h-full flex flex-col min-h-0" style={{ minHeight: 0 }}>
        <ScheduleViewContent
          scheduleUuid={scheduleUuid}
          mode="optimized"
          isOpen={isOpen}
          contentHeightPx={contentHeightPx}
        />
      </div>
    </DialogComponent>
  );
};

export default OptimizedScheduleViewDialog;
